import * as React from "react";
import {
    DefaultButton,
    Icon,
    ILabelStyles,
    Label,
    Panel,
    PrimaryButton,
    Stack,
    TextField,
} from "@fluentui/react";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../../redux";
import emailMapper from "../../services/EmailMapper";
import { emailMappingTypes } from "../../services/EmailMapper";
import { setEmailToMetaDataMapping } from "../../redux/modules/user";
import LZString = require("lz-string");
import { logger } from "../../../../utilities/logger";

const labelStyle: ILabelStyles = {
    root: {
        padding: 0,
        margin: 0
    }
}

const mapDispatchToProps = {};

const mapStateToProps = (state: RootState) => {
    return {
        accountId: state.user.account.accountId,
        tenantEmailToMetadataMapping: state.tenantInfo.emailToMetadataMapping
    }
}

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface EmailMappingProps extends PropsFromRedux {
    isOpen: boolean,
    closePanel: () => void,
}

const EmailMappingPanel: React.FunctionComponent<EmailMappingProps> = ({
    isOpen,
    closePanel,
    accountId,
    tenantEmailToMetadataMapping
}) => {
    const languageStrings = React.useContext(LanguageContext);
    const [columnVals, setColumnVals] = React.useState<string[]>();
    const [isDirty, setIsDirty] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (isOpen) {
            setIsDirty(false);
            setColumnVals(Object.keys(emailMappingTypes).map(key => emailMapper.getColumnValue(key)?.split(",").map(v => v.trim()).join(", ")));
        }
    }, [isOpen]);

    const loadDefaults = React.useCallback(() => {
        try {
            let fieldColumnMap: any[];
            if (tenantEmailToMetadataMapping) {
                const tenantMailMappings: string = LZString.decompressFromBase64(tenantEmailToMetadataMapping);
                fieldColumnMap = JSON.parse(tenantMailMappings);
            }
            else {
                fieldColumnMap = Object.keys(emailMappingTypes).map(key => {
                    return ({
                        name: key,
                        value: emailMappingTypes[key],
                    })
                });
            }
            setIsDirty(true);
            setColumnVals(fieldColumnMap.map(field => field.value?.split(",").map(v => v.trim()).join(", ")));
        } catch (error) {
            logger(error, "EmailMappingPanel (loadDefaults)");
        }
    }, [setColumnVals, tenantEmailToMetadataMapping]);

    const buildEmailCategoryItems = () => {
        const items = Object.keys(emailMappingTypes).map((type, index) => {
            //use typeof emailMappingTypes[type] to determine type of field, of necessary.
            return (
                <Stack key={index} tokens={{ childrenGap: 5 }} verticalAlign="space-between">
                    <Stack horizontal verticalAlign="center">
                        <Icon
                            iconName={emailMapper.emailTypeIconNameResolver(type)}
                            style={{
                                fontSize: 19,
                                marginRight: 7,
                            }}
                            styles={{
                                root: {
                                    marginBottom: 0,
                                    marginTop: 0,
                                }

                            }}

                        />
                        <Label styles={labelStyle}>
                            {emailMapper.emailMappingCategoryNameResolver(languageStrings, type)}
                        </Label>
                    </Stack>
                    <TextField
                        value={columnVals[index]}
                        onChange={(_, nv) => {
                            setIsDirty(true);
                            setColumnVals(prevVals => {
                                const newArr = [...prevVals];
                                newArr[index] = nv;
                                return newArr;
                            });
                        }}
                    />
                </Stack>
            )
        })
        return items;
    }

    const emailMappingItems = React.useMemo(() => {
        if (columnVals) {
            return buildEmailCategoryItems();
        }
        return null;
    }, [columnVals]);

    const onSave = async () => {
        Object.keys(emailMappingTypes).forEach((type, index) => {
            emailMapper.setColumnNameValue(type, columnVals[index]?.split(",").map(v => v.trim()).join(","));
        });
        const emailString = emailMapper.getEmailMappingString();
        setEmailToMetaDataMapping(accountId, emailString);
        closePanel();
    }

    const onRenderFooterContent = React.useCallback(
        () => (
            <Stack horizontal tokens={{ childrenGap: 8 }}>
                <PrimaryButton
                    disabled={!isDirty}
                    text={languageStrings.Save}
                    onClick={onSave}
                />
                <DefaultButton
                    text={languageStrings.LoadDefaults}
                    onClick={loadDefaults}
                >
                </DefaultButton>
            </Stack>
        ),
        [onSave]
    );

    return (
        <Panel
            headerText={languageStrings.EmailHeaderMapping}
            isOpen={isOpen}
            onDismiss={closePanel}
            closeButtonAriaLabel="Close"
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            {emailMappingItems &&
                <Stack styles={{ root: { paddingTop: 10 } }} tokens={{ childrenGap: 15 }}>
                    {emailMappingItems}
                </Stack>}
        </Panel>
    )
};

export default connector(EmailMappingPanel);